import Player from './Player'
import Emitter from './Emitter'

export const UIClasses = {
  controlbar: 'fp-controls',
  elements: {
    'play': 'fp-btns fp-small-switch fc-play-button',
    'rewind': 'fc-icon-rewind',
    'next': '',
    'live': 'fp-live-status',
    'timeline': ['fp-elapsed', 'fp-timeline', 'fp-duration'],
    'elapsed': 'fp-elapsed',
    'duration': 'fp-duration',
    'timeslider': 'fp-timeline',
    'quality': 'fp-qsel',
    'captions': 'fp-cc',
    'audiotracks': 'fp-asel',
    'volume': ['fp-volumebtn', 'fp-volume'], // theses elements can be switched with inverseVolumebar()
    'cast': 'fp-airplay',
    'chapters': 'fc-icon-chapters',
    'transcript': 'fc-icon-transcript',
    'fullscreen': 'fp-togglable fc-fullscreen-button',
    'popout': 'fp-popout'
  }
}

class FlowNPlayer extends Player {
  init () {
    this.playerType = 'FlowPlayer'
    this.container = this.source.parentElement
    this.source._lock = 0

    super.init()
  }

  getUIClasses () {
    return UIClasses
  }

  prepareElement () {
    let _this = this
    let _source = this.source

    let _load = _source.load

    function load (src) {
      // this flag will be used in order to know when to removePosterFrame()
      // @see _source.on('loadedmetadata') callback function
      _source.isSettingSrc = true
      if (typeof src === 'string') {
        let m = src.match(/\.([a-z0-9]+)(?:\?.*)?$/i)
        if (m) {
          let ext = m[1].toLowerCase()
          let type

          switch (ext) {
            case 'f4m':
              type = 'application/f4m'
              break
            case 'm3u8':
              type = 'application/x-mpegURL'
              break
            default:
              type = 'video/' + ext
              break
          }

          // As I see it, this is needed for some legacy scripts.
          // To avoid it to generate errors, we need to wrap it
          if (_source.opts.item && _source.opts.item.file) {
            _source.opts.item.file = src
          }
          src = { type: type, src: src }

          _source.player.setSrc(src)
          loaded()
        }
      }
      else if (typeof src === 'object') {
        _source.opts.item = src
        src = src.sources.map(s => {
          let r = { src: s.file }
          if (s.label === 'HLS') {
            r.type = 'application/x-mpegurl'
          }
          else if (s.label === 'dash') {
            r.type = 'application/dash+xml'
          }
          else {
            r.type = 'video/mp4'
          }
          return r
        })

        _source.player.setSrc(src)

        loaded()
      }
      else {
        _load.call(_source, src)
      }
    }

    function loaded () {
      if (_source.opts.item && _source.opts.item.sources && _source.opts.item.sources.length)
      {  _source.opts.item.file = _source.opts.item.sources[0].file }

      _source.trigger('loadedmetadata', { item: _source.opts.item })
      displayPosterframe()
    }

    function displayPosterframe () {
      setTimeout(() => {
        _source.root.classList.add('is-starting')
        _source.style.display = 'none'
      }, 0)
    }

    function inverseVolumebar() {
      Object.assign(UIClasses.elements.volume, ['fp-volume', 'fp-volumebtn'])
      _source.root.classList.add('inverted-horizontal-vol')
    }

    function removePosterFrame () {
      setTimeout(() => {
        _source.root.classList.remove('is-starting')
        _source.style.display = 'block'
      }, 0)
    }

    function reorderUI (order) {
      let previousEl = undefined;
      let controlbar = this.container.getElementsByClassName(UIClasses.controlbar)[0]

      // Put each element in the controlbar, one after the other
      order.forEach(function (name) {
        let className = UIClasses.elements[name]
        if (Array.isArray(className)) {
          className.forEach((e) => { reinsert(e) })
        } else {
          reinsert(className)
        }
      })

      function reinsert (className) {
        let element = controlbar.getElementsByClassName(className)

        if (element && element.length != 0) {
          element = element[0]

          if (previousEl) {
            controlbar.insertBefore(element, previousEl.nextSibling)
          } else {
            controlbar.prepend(element)
          }
          previousEl = element
        }
      }
    }

    function insertIcon (icon, position) {
      let target = position.before ? position.before : position.after
      target = UIClasses.elements[target];
      let controlbar = this.container.getElementsByClassName(UIClasses.controlbar)[0];

      if (!controlbar) return
      let targetEl = controlbar.getElementsByClassName(target)[0]

      if (targetEl === undefined) {
        return;
      }

      if (position.before) {
        controlbar.insertBefore(icon, targetEl)
      }
      else {
        controlbar.insertBefore(icon, targetEl.nextSibling)
      }
    }

    function getOverlay () {
      let overlayContainer = this.container.getElementsByClassName('fc-poster')

      if (!overlayContainer || overlayContainer.length === 0) {
        overlayContainer = document.createElement('div')
        overlayContainer.classList.add('fc-overlays')
        overlayContainer.classList.add('fc-poster')
        overlayContainer.style.cssText = 'height: auto; top: 0px; right: 0px; bottom: 0px; left: 0px; position: absolute; width: auto; font-size: 22.5px;pointer-events:none;'
        this.container.prepend(overlayContainer)

        return overlayContainer
      } else {
        return overlayContainer[0]
      }
    }

    function lock (id) {
      if (!this._lock) {
        this.had_controls = this.controls
      }

      this._lock |= id

      this.container.classList.add('fc-locked')

      displayPosterframe()

      this.controls = false
      this.pause()
      this.player.toggleDisable(true)
      this.trigger('playerlocked', id)
    }

    function unlock (id) {
      if (!this._lock) {
        return false
      }

      this._lock &= ~id

      if (!this._lock) {
        this.controls = this.had_controls
        this.player.toggleDisable(false)

        this.container.classList.remove('fc-locked')

        removePosterFrame()

        this.trigger('playerunlocked')
        return true
      }
      return false
    }

    this.element.trigger = function (eventType, eventData = {}) {
      this.dispatchEvent(Emitter.createEvent('Event', eventType, eventData))
      _source.trigger(eventType, eventData)
    }

    super.prepareElement()

    let events = {}

    let methods = {
      /* HTMLMediaElement methods */
      'exitFullScreen': function () {
        _source.player.toggleFullScreen(false)
      },
      'requestFullScreen': function () {
        _source.player.toggleFullScreen(true)
      },
      'play': function () {
        _source.play()
      },
      'pause': function () {
        _source.pause()
      },
      'destroy': function () {
        _source.destroy()
      },
      'insertIcon': insertIcon,
      'reorderUI': reorderUI,
      'lock': lock,
      'unlock': unlock,
      'load': load,
      'inverseVolumebar': inverseVolumebar
    }
    let properties = {

      'id': {
        get: function () {
          return _source.root.id
        },
      },
      'config': {
        get: function () {
          return _source.opts
        },
      },
      'currentConfig': {
        get: function () {
          return _this.mergeObjects(_source.opts, _source.opts.item)
        },
      },
      'controls': {
        get: function () {

          return _source.root.ui === undefined ? false : _source.root.ui.style.display !== 'none'
        },
        set: function (value) {
          _source.root.ui.style.display = (!!value) ? 'flex' : 'none'
          return true
        },
      },
      'overlay': {
        get: getOverlay
      },

      /* HTMLMediaElement properties */
      'currentSrc': {
        get: function () {
          return _source.currentSrc
        }
      },
      'duration': {
        get: function () {
          return _source.duration
        }
      },
      'ended': {
        get: function () {
          return _source.ended
        }
      },
      'paused': {
        get: function () {
          return _source.paused
        }
      },
      'muted': {
        get: function () {
          return _source.muted
        },
        set: function (value) {
          _source.muted = value
        }
      },
      'volume': {
        get: function () {
          return _source.volume
        },
        set: function (value) {
          _source.volume = value
        }
      },
      'currentTime': {
        get: function () {
          return _source.currentTime
        },
        set: function (value) {
          _source.currentTime = value
        }
      },
      'loop': {
        get: function () {
          return _source.loop
        },
        set: function (value) {
          _source.loop = value
        }
      },
      'playbackRate': {
        get: function () {
          return _source.playbackRate
        },
        set: function (value) {
          _source.playbackRate = value
        }
      },
      'audioTracks': {
        get: function () {
          if (_source.asel) {
            return _source.asel.tracks;
          }

          console.log('audioTracks is not implemented')
          return []
        },
      },
      'defaultMuted': {
        get: function () {
          return _source.opts.muted === undefined ? false : _source.opts.muted
        },
        set: function (value) {
          _source.opts.muted = !!value
          return true
        },
      },
      'poster': {
        get: function () {
          let root = _source.root

          if (root && root.style) {
            let backgroundImage = root.style.backgroundImage

            if (typeof backgroundImage === 'string') {
              let matches = backgroundImage.match(/url\(["']?(.*?)["']?\)/) || []
              if (matches.length === 2) {
                return matches[1]
              }
            }
          }

          return ''
        },
        set: function (value) {
          let elem = _source.root
          if (elem) {
            elem.style.backgroundImage = 'url("' + value + '")'
            elem.style.backgroundSize = 'cover'
          }
          _source.setOpts({ poster: value })
        }
      }
    }

    this.updateMirroredElement()

    for (let key in events) {
      this.mapEvent(key, events[key])
    }

    for (let key in methods) {
      this.mapMethod(key, methods[key])
    }

    for (let key in properties) {
      this.mapProperty(key, properties[key])
    }

    _source.on('playing play', (e) => {
      // Prevent any playback when the player is locked
      if (this._lock) {
        this.player.toggleDisable(true)
        this.pause()
      }
    })
    _source.on('loadedmetadata', (event) => {
      // we need to catch the "real" loadedmetadata, not the one loaded() sent
      if (event.hasOwnProperty('item')) return
      if (_source.isSettingSrc === true) {
        removePosterFrame()
        _source.isSettingSrc = false
      }
    })

    // Set the item.file to the first source, for legacy plugins
    if (_source.opts.item && _source.opts.item.sources && _source.opts.item.sources.length)
    { _source.opts.item.file = _source.opts.item.sources[0].file }
  }

  updateMirroredElement() {
    this.setMirroredElement(this.container.getElementsByTagName('video').item(0))
  }

  static handles(source) {
    if (typeof source === 'object' && source instanceof HTMLVideoElement) {
      if (source.classList.contains('fp-engine') && source.tagName === 'VIDEO') {
        return true
      }
    }
  }
}

export default FlowNPlayer
